import { MeetingView, NegotiationStatus, ParticipantStateView } from "@/api/types";

/**
 * Visual + textual presentation per negotiation status. The wire enum is
 * OPEN | ACCEPTED | FAILED | EXPIRED — these styles map each one to a
 * comic-friendly colour pair + label.
 */
export const MEETING_STATE_STYLES: Record<
  NegotiationStatus,
  { bg: string; border: string; label: string }
> = {
  // Tint weight is kept as it was (a -100 becomes /10, a -200 becomes /20) so this
  // stays a token migration. Normalising the weight across states is a craft-pass
  // decision, not this one's.
  OPEN:      { bg: "bg-info/20",    border: "border-info",            label: "Pending" },
  ACCEPTED:  { bg: "bg-success/20", border: "border-success",         label: "Confirmed" },
  FAILED:    { bg: "bg-destructive/10", border: "border-destructive", label: "Failed" },
  EXPIRED:   { bg: "bg-muted",      border: "border-muted-foreground", label: "Expired" },
  CANCELLED: { bg: "bg-muted",      border: "border-muted-foreground", label: "Cancelled" },
  AWAITING_INITIATOR: { bg: "bg-warning/10", border: "border-warning", label: "Needs you" },
};

/** States that are still actively negotiating (no terminal outcome yet). */
export function isLiveNegotiation(state: NegotiationStatus): boolean {
  return state === "OPEN" || state === "AWAITING_INITIATOR";
}

/**
 * What a reschedule that did not happen is called, in the asker's own words.
 *
 * <p>FAILED and EXPIRED both leave the meeting where it was, and for a long time the UI said the
 * same sentence for both. It was only ever true of one of them. FAILED means every calendar was
 * compared and no time fitted. EXPIRED means the other side never answered and the attempt was
 * given up on: nothing was compared, so "nobody was free" reports a result nobody obtained.</p>
 *
 * <p>The distinction became visible the day something started writing EXPIRED (the negotiation
 * expiry sweep). Before that this branch was unreachable, which is exactly why the wrong wording
 * survived so long.</p>
 */
export function rescheduleGaveUpBanner(state: NegotiationStatus): string {
  return state === "EXPIRED"
    ? "Gave up on moving this. Nobody answered in time. This meeting is unchanged."
    : "Couldn't find a new time. Nobody was free in the window you asked for. This meeting is unchanged.";
}

/** The same fact told to a participant who did not ask for the move. */
export function rescheduleGaveUpToast(state: NegotiationStatus, subject: string): string {
  return state === "EXPIRED"
    ? `A new time was tried for "${subject}", but nobody answered in time, so it's unchanged.`
    : `A new time was checked against your calendar for "${subject}", but nobody was free, so it's unchanged.`;
}

/**
 * iCalendar (RFC 5545) event STATUS — the three-value vocabulary every calendar
 * that matters (Google, Microsoft, CalDAV) speaks. Mapping our richer
 * negotiation states down to it here is the seam that keeps a future two-way
 * sync honest: the block a peer sees in their Google Calendar is exactly this
 * status. It is also what the calendar view styles on — tentative is hatched,
 * confirmed is solid, cancelled is faded.
 *
 * <p>OPEN / AWAITING_INITIATOR are still being negotiated → TENTATIVE;
 * ACCEPTED is booked → CONFIRMED; everything that fell through — CANCELLED,
 * FAILED, EXPIRED — reads as CANCELLED.</p>
 */
export type ICalStatus = "TENTATIVE" | "CONFIRMED" | "CANCELLED";

export function icalStatus(state: NegotiationStatus): ICalStatus {
  switch (state) {
    case "ACCEPTED":
      return "CONFIRMED";
    case "OPEN":
    case "AWAITING_INITIATOR":
      return "TENTATIVE";
    default:
      return "CANCELLED";
  }
}

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
export const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

export function formatTime(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function formatDateLabel(d: Date): string {
  return `${WEEKDAYS[(d.getDay() + 6) % 7]} ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export function startOfWeek(d: Date): Date {
  // ISO week starts Monday.
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  const dow = (out.getDay() + 6) % 7;
  out.setDate(out.getDate() - dow);
  return out;
}

/**
 * UI-side representation of a MeetingView with already-parsed Date objects
 * and a single canonical "when" — the decided slot if accepted, else the
 * created-at timestamp so non-accepted rows still have a place on the
 * timeline.
 */
/**
 * What the reschedule picker collects: a date span plus an optional clock-time
 * band. When {@link timeStart}/{@link timeEnd} are set the agent is confined to
 * that band each day (a conscious priority exception to the availability-pattern window).
 */
export interface ReschedulePick {
  startDate: string; // ISO date
  endDate: string; // ISO date (== startDate for a single day)
  timeStart?: string; // HH:MM
  timeEnd?: string; // HH:MM
  /**
   * True = this exact band, or nothing. Overrides both parties' availability-pattern
   * hours, and the reschedule fails outright when nobody can make it.
   * False = a preference: search inside the band first, widen if need be.
   */
  strict?: boolean;
}

export interface UiMeeting {
  id: string;
  state: NegotiationStatus;
  participants: string[];
  availabilityPattern: string | null;
  subject: string | null;
  detail: string | null;
  seriesId: string | null;
  start: Date | null;
  end: Date | null;
  createdAt: Date;
  acceptedAt: Date | null;
  /** True when a later reschedule superseded this negotiation. */
  superseded: boolean;
  /** Per-counterparty states (group meetings); empty for legacy 1:1 rows. */
  participantStates: ParticipantStateView[];
  /** Offered times to pick from when this meeting is at an impasse. */
  counterOptions: { start: Date; end: Date }[];
  /** Display name of whoever arranged the meeting, or null on legacy rows. */
  createdBy: string | null;
  /** True when the signed-in user created this meeting. Only they may pin a window. */
  createdByMe: boolean;
  /** The chain this meeting belongs to; every reschedule of it shares this id. */
  rootMeetingId: string | null;
  /** The meeting this one is replacing, when it is a reschedule. */
  rescheduledFrom: string | null;
  /** True when the signed-in user is the one who triggered this meeting's reschedule. */
  rescheduledByMe: boolean;
  /** A reschedule of THIS meeting that is still negotiating. */
  pendingReschedule?: UiMeeting | null;
  /** A reschedule of THIS meeting that found no time; the meeting is unchanged. */
  failedReschedule?: UiMeeting | null;
  /** Attempts already behind this meeting in the chain, collapsed out of the list. */
  earlierAttempts?: number;
}

export function toUiMeeting(m: MeetingView): UiMeeting {
  const start = m.decided_slot ? new Date(m.decided_slot.start) : null;
  const end = m.decided_slot ? new Date(m.decided_slot.end) : null;
  return {
    id: m.conversation_id,
    state: m.state,
    participants: m.with,
    availabilityPattern: m.pattern_label,
    subject: m.subject,
    detail: m.detail,
    seriesId: m.series_id,
    start,
    end,
    createdAt: new Date(m.created_at),
    acceptedAt: m.accepted_at ? new Date(m.accepted_at) : null,
    superseded: m.superseded ?? false,
    participantStates: m.participant_states ?? [],
    counterOptions: (m.counter_options ?? []).map((s) => ({
      start: new Date(s.start),
      end: new Date(s.end),
    })),
    createdBy: m.created_by ?? null,
    createdByMe: m.created_by_me ?? false,
    rootMeetingId: m.root_meeting_id ?? null,
    rescheduledFrom: m.rescheduled_from ?? null,
    rescheduledByMe: m.rescheduled_by_me ?? false,
  };
}

/**
 * Collapse each reschedule chain into the one meeting that still matters.
 *
 * <p>A reschedule opens a NEW negotiation and leaves the original booked until a
 * new time is agreed, so the raw list can hold a booked meeting and one or more
 * attempts to move it. Rendering those as sibling rows is why a reschedule looked
 * like the meeting had vanished and been replaced by a stranger.</p>
 *
 * <p>Each chain (grouped by {@code rootMeetingId}) collapses to its current
 * meeting — the booked one if there is one, else the newest live negotiation. An
 * attempt still negotiating hangs off it as {@code pendingReschedule}; one that
 * failed as {@code failedReschedule}, which is what lets the row say "I couldn't
 * move it, and I've left it where it was".</p>
 */
export function collapseRescheduleChains(meetings: UiMeeting[]): UiMeeting[] {
  const chains = new Map<string, UiMeeting[]>();
  for (const m of meetings) {
    const root = m.rootMeetingId ?? m.id;
    const bucket = chains.get(root);
    if (bucket) bucket.push(m);
    else chains.set(root, [m]);
  }

  const out: UiMeeting[] = [];
  for (const chain of chains.values()) {
    if (chain.length === 1) {
      out.push(chain[0]);
      continue;
    }
    const newestFirst = [...chain].sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
    );
    const current =
      newestFirst.find((m) => m.state === "ACCEPTED") ??
      newestFirst.find((m) => isLiveNegotiation(m.state)) ??
      newestFirst[0];

    const attempts = newestFirst.filter((m) => m.id !== current.id);
    out.push({
      ...current,
      pendingReschedule:
        attempts.find(
          (m) => m.rescheduledFrom === current.id && isLiveNegotiation(m.state),
        ) ?? null,
      failedReschedule:
        attempts.find(
          (m) =>
            m.rescheduledFrom === current.id &&
            (m.state === "FAILED" || m.state === "EXPIRED"),
        ) ?? null,
      earlierAttempts: attempts.length,
    });
  }
  return out;
}

/** The short form of a negotiation id the UI shows, e.g. "#2a013ed2". */
export function shortId(id: string): string {
  return `#${id.slice(0, 8)}`;
}

/** Short label + tone for a participant state chip. */
export function participantStateChip(
  state: ParticipantStateView["state"],
): { label: string; cls: string } {
  switch (state) {
    case "ACCEPTED":
      return { label: "in", cls: "bg-success/10 text-success-strong border-success/30" };
    case "REJECTED":
    case "DECLINED":
      return { label: "declined", cls: "bg-destructive/10 text-destructive-strong border-destructive/30" };
    case "INCOMPATIBLE_SCHEDULE":
      return { label: "can't make it", cls: "bg-warning/10 text-warning-strong border-warning/30" };
    case "NO_RESPONSE":
      return { label: "no reply", cls: "bg-muted text-muted-foreground border-border" };
    case "CANCELLED":
      return { label: "cancelled", cls: "bg-muted text-muted-foreground border-border" };
    default:
      return { label: "pending", cls: "bg-info/10 text-info-strong border-info/30" };
  }
}

/** Headline title: the parsed subject when present, else derived from participants. */
export function meetingTitle(m: UiMeeting): string {
  if (m.subject && m.subject.trim()) return m.subject;
  if (m.participants.length === 0) return "Meeting";
  if (m.participants.length === 1) return `Meeting with ${m.participants[0]}`;
  return `Meeting with ${m.participants[0]} + ${m.participants.length - 1}`;
}

/**
 * The availability pattern as the user wrote it. Types are free text a person chose for
 * themselves, so there is nothing to prettify — this exists only to give the one
 * null-handling decision a single home. Null on a meeting we were invited to:
 * how the sender categorised it never crosses the wire.
 */
export function availabilityPatternLabel(t: string | null): string | null {
  return t && t.trim() ? t : null;
}

/** Best date to bucket / sort by: decided slot if available, else created. */
export function meetingPivot(m: UiMeeting): Date {
  return m.start ?? m.createdAt;
}

export function durationMinutes(m: UiMeeting): number | null {
  if (!m.start || !m.end) return null;
  return Math.round((m.end.getTime() - m.start.getTime()) / 60000);
}

/** Query param the calendar reads to jump to, select, and highlight one meeting. */
export const FOCUS_MEETING_PARAM = "meeting";

/**
 * Deep link to a booked meeting on the calendar. The single source of truth for
 * this URL, so the chat's "It's on your calendar" line and the negotiation
 * drawer's footer can never drift apart.
 *
 * <p>A conversation id IS the meeting id — the negotiation is the meeting.</p>
 */
export function calendarLinkFor(conversationId: string): string {
  return `/meetings?view=calendar&${FOCUS_MEETING_PARAM}=${encodeURIComponent(conversationId)}`;
}
